<script setup lang="ts">
import { officesData } from './career-main-offices.consts'

const { t } = useI18n()
const { isDesktop } = useBSBreakpoints()

const activeOfficeId = ref<string | null>(officesData[0].id)

const activeOffice = computed(() =>
  officesData.find((o: { id: string | null }) => o.id === activeOfficeId.value),
)

const tooltipOffset = computed(() => {
  if (!activeOffice.value) return { top: '0', left: '0' }

  const topOffset = isDesktop.value ? '1.25rem' : '0.75rem'

  return {
    top: `calc(${activeOffice?.value.coords.top} + ${topOffset})`,
    left: activeOffice.value.coords.left,
  }
})

const toggleTooltip = (id: string) => {
  activeOfficeId.value = activeOfficeId.value === id ? null : id
}

const closeTooltip = () => {
  activeOfficeId.value = null
}

const handleEscape = (e: KeyboardEvent) => {
  if (e.key === 'Escape') {
    closeTooltip()
  }
}

onMounted(() => {
  window.addEventListener('keydown', handleEscape)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleEscape)
})
</script>

<template>
  <section class="career-offices">
    <div class="career-offices__container">
      <div class="career-offices__map-column">
        <div class="career-offices__map-container">
          <img
            src="/images/career/offices/poland_map.jpg"
            :alt="t('career.offices.mapAlt')"
            class="career-offices__map-svg"
          >

          <button
            v-for="office in officesData"
            :key="office.id"
            class="career-offices__point"
            :class="{ 'career-offices__point--active': activeOfficeId === office.id }"
            :style="{ top: office.coords.top, left: office.coords.left }"
            :aria-label="`Biuro ${office.city}`"
            :aria-expanded="activeOfficeId === office.id"
            :aria-describedby="activeOfficeId === office.id ? 'office-tooltip' : undefined"
            aria-haspopup="true"
            @click="toggleTooltip(office.id)"
          />

          <Transition name="fade">
            <div
              v-if="activeOffice"
              id="office-tooltip"
              class="career-offices__tooltip"
              role="tooltip"
              aria-live="polite"
              :aria-label="`${activeOffice.city} ${activeOffice.address}, ${activeOffice.postalCode}`"
              :style="{
                top: tooltipOffset.top,
                left: tooltipOffset.left,
              }"
            >
              <h3 class="career-offices__tooltip-title">
                {{ activeOffice.city }}
              </h3>

              <p class="career-offices__tooltip-address">
                {{ activeOffice.address }},<br>
                {{ activeOffice.postalCode }}
              </p>

              <NuxtLink
                :to="activeOffice.link"
                target="_blank"
              >
                <UiButton
                  size="small"
                  variant="secondary"
                  class="career-offices__tooltip-btn"
                >
                  {{ t('career.offices.navigate') }}
                </UiButton>
              </NuxtLink>
            </div>
          </Transition>
        </div>
      </div>

      <div class="career-offices__content-column">
        <UiHeader
          :header="$t('career.offices.title') "
          position="left"
          header-size="h5"
          header-size-tablet="h5"
          header-size-mobile="h5"
        />

        <p class="career-offices__description">
          {{ t('career.offices.description') }}
        </p>

        <div
          class="career-offices__list"
          role="group"
          :aria-label="t('career.offices.citiesList')"
        >
          <button
            v-for="office in officesData"
            :key="office.id"
            class="career-offices__list-item"
            :class="{ 'career-offices__list-item--active': activeOfficeId === office.id }"
            :aria-pressed="activeOfficeId === office.id"
            :aria-describedby="activeOfficeId === office.id ? 'office-tooltip' : undefined"
            @click="activeOfficeId = office.id"
          >
            <span
              class="career-offices__bullet"
              aria-hidden="true"
            />

            <span class="career-offices__city-name">
              {{ office.city }}
            </span>
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.career-offices {
  padding: 0 2.4rem;

  &__container {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 3.2rem;
    max-width: 136rem;
    margin: 0 auto;
  }

  &__map-column {
    position: relative;
    border-radius: 3.2rem;
  }

  &__map-container {
    position: relative;
    width: 100%;
    margin: 0 auto;
  }

  &__map-svg {
    display: block;
    filter: drop-shadow(0 2rem 4rem rgba(0, 0, 0, 0.05));
  }

  &__point {
    position: absolute;
    z-index: 2;
    width: 2.8rem;
    height: 2.8rem;
    padding: 0;
    border: none;
    border-radius: 0.8rem;
    transform: translate(-50%, -50%);
    cursor: pointer;
    transition: all 0.2s ease-in-out;

    &:focus-visible {
      z-index: 3;
      outline: 0.3rem solid $purple-500;
      outline-offset: 0.4rem;
    }

    &::after {
      position: absolute;
      top: 50%;
      left: 50%;
      width: 1.25rem;
      height: 1.25rem;
      background: $white;
      border: 0.4rem solid $purple-500;
      border-radius: 50%;
      transform: translate(-50%, -50%);
      transition: all 0.2s;
      content: '';
    }

    &:hover,
    &--active,
    &:focus-visible {
      &::after {
        width: 1.75rem;
        height: 1.75rem;
        background: $purple-300;
        border: 0.4rem solid $purple-500;
      }
    }
  }

  &__tooltip {
    position: absolute;
    z-index: 10;
    width: 17.5rem;
    padding: 1.2rem;
    background: $white;
    border-radius: 0.8rem;
    box-shadow: 0 1rem 1.2rem 0 rgba(0, 0, 0, 0.14);
    transform: translate(-50%, calc(-100% - 1.2rem));

    &::after {
      position: absolute;
      bottom: -0.4rem;
      left: 50%;
      display: block;
      border-top: 0.8rem solid $white;
      border-right: 1rem solid transparent;
      border-left: 1rem solid transparent;
      transform: translateX(-50%);
      content: '';
    }

    &-title {
      @include h9;
      margin-bottom: 0.4rem;
      color: $darkblue-400;
    }

    &-address {
      @include body-2;
      margin-bottom: 1.2rem;
      color: $darkblue-500;
      font-size: 1.1rem;
      line-height: 1.4;
    }

    &-btn {
      @include text-link;
      color: $brand-blue-500;
      background: $light-purple-100;
      border: none;

      :deep() {
        min-width: auto;
        padding: 0.8rem 1.6rem;
      }
    }
  }

  &__content-column {
    flex: 1;
    order: -1;
  }

  &__description {
    @include body-1-medium;
    margin-top: 1rem;
    margin-bottom: 2.4rem;
    color: $text-500;
  }

  &__list {
    display: grid;
    grid-template-columns: 1fr;
    gap: 2.4rem;
  }

  &__list-item {
    display: flex;
    align-items: center;
    gap: 1.2rem;
    padding: 0;
    text-align: left;
    border: none;
    border-radius: 0.8rem;
    cursor: pointer;
    transition: background-color 0.2s;

    &:focus-visible {
      outline: 0.2rem solid $purple-500;
      outline-offset: 0.4rem;
    }

    &--active {
      .career-offices__city-name {
        color: #2D392D;
        font-weight: 700;
      }

      .career-offices__bullet {
        &::after {
          width: 1.75rem;
          height: 1.75rem;
          background: $purple-300;
          border: 0.4rem solid $purple-500;
        }
      }
    }
  }

  &__bullet {
    position: relative;
    flex-shrink: 0;
    width: 2.8rem;
    height: 2.8rem;
    background: #F3EBFF;
    border-radius: 0.8rem;
    transition: all 0.2s;

    &::after {
      position: absolute;
      top: 50%;
      left: 50%;
      width: 1.25rem;
      height: 1.25rem;
      background: $white;
      border: 0.4rem solid $purple-500;
      border-radius: 50%;
      transform: translate(-50%, -50%);
      transition: all 0.2s;
      content: '';
    }
  }

  &__city-name {
    @include body-1;
    color: $text-500;
    transition: color 0.2s;
  }

  @include atXS {
    &__list {
      grid-template-columns: repeat(2, 20rem);
      padding-bottom: 4rem;
    }
  }

  @include atM {
    &__container {
      flex-direction: row;
      align-items: center;
      gap: 6.4rem;
    }

    &__map-column {
      display: block;
      flex: 1 1 50%;
    }

    &__tooltip {
      width: 22rem;
      transform: translate(-50%, calc(-100% - 2rem));

      &-title {
        margin-bottom: 0.8rem;
      }

      &-address {
        margin-bottom: 1.6rem;
      }

      &-btn {
        &::before {
          display: none;
        }
      }
    }

    &__map-container {
      max-width: 57rem;
      width: fit-content;
      max-height: 64rem;
      margin-left: auto;
    }

    &__map-svg {
      width: 100%;
      height: 100%;
    }

    &__content-column {
      flex: 1 1 50%;
      order: 0;
    }

    &__description {
      margin-bottom: 4.8rem;
    }
  }
}
</style>
